"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils.js";
import { Z_INDEX } from "@/z-index.js";

/**
 * Tooltip provider component
 */
const TooltipProvider = TooltipPrimitive.Provider;

/**
 * Tooltip root component
 */
const Tooltip = TooltipPrimitive.Root;

/**
 * Tooltip trigger component
 */
const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * Tooltip content component - The actual tooltip popup
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {number} [props.sideOffset] - Distance from trigger
 * @param {React.Ref<HTMLDivElement>} ref - Forwarded ref
 * @param {...Object} props - Other tooltip content props
 * @returns {JSX.Element} Tooltip content
 */
const TooltipContent = React.forwardRef(
  /** @param {{ className?: string, sideOffset?: number, children?: React.ReactNode, [key: string]: any }} props */
  ({ className, sideOffset = 4, ...props }, ref) => (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "z-tooltip overflow-hidden rounded-md border border-border bg-popover px-3 py-2 text-sm text-popover-foreground shadow-lg animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]",
          className
        )}
        style={{ zIndex: Z_INDEX.tooltip }}
        {...props}
      />
    </TooltipPrimitive.Portal>
  )
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
